import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';

import { useAuth, useDictionary, usePage } from '@/src/app/hooks';
import { Container } from '@/src/ui';
import { type TextbookItemDTO } from '../lessons/DTOs';
import './LessonsPage.css';

import gTKCover from '@/src/assets/img/lessons/cover-grade-TK.jpg';
import gKCover from '@/src/assets/img/lessons/cover-grade-K.jpg';
import g1Cover from '@/src/assets/img/lessons/cover-grade-1.jpg';
import g2Cover from '@/src/assets/img/lessons/cover-grade-2.jpg';
import g3Cover from '@/src/assets/img/lessons/cover-grade-3.jpg';
import g4Cover from '@/src/assets/img/lessons/cover-grade-4.jpg';
import g5Cover from '@/src/assets/img/lessons/cover-grade-5.jpg';
import g6Cover from '@/src/assets/img/lessons/cover-grade-6.jpg';
import g6MCover from '@/src/assets/img/lessons/cover-grade-6M.jpg';
import g7Cover from '@/src/assets/img/lessons/cover-grade-7.jpg';
import g8Cover from '@/src/assets/img/lessons/cover-grade-8.jpg';

const LessonsPage: React.FC = () => {
    const { setSubheaderTitle, setSubheaderType, pageLanguage } = usePage();
    const { user } = useAuth();
    const { translate } = useDictionary();
    document.title = translate('Lessons', 'Lessons');

    useEffect(() => {
        setSubheaderTitle(translate('Lessons'));
        setSubheaderType('default');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [translate]);

    const assignedBooks = useMemo(() => {
        if (!user?.allowedGrades) return [];

        const elementaryGrades = user.allowedGrades.elementary || [];
        const middleSchoolGrades = user.allowedGrades.middleSchool || [];

        return [...elementaryGrades, ...middleSchoolGrades].map((g) => {
            const gradeObj: TextbookItemDTO = {
                image: `/Content/assets/img/lessons/cover-grade-${g}.jpg`,
                code: g,
                viewAllLessonsLink: `/allLessons/${g}`,
                name: translate('Grade') + ' ' + g,
            };

            if (g === 'TK' || g === 'K') {
                gradeObj.teLink1 = {
                    displayText: 'Vol 1',
                    link: `https://teacher-files.paradigmmath.com/${pageLanguage}/_flipbook/TE/G${g}-TE-Vol-1/index.html`,
                };
                gradeObj.teLink2 = {
                    displayText: 'Vol 2',
                    link: `https://teacher-files.paradigmmath.com/${pageLanguage}/_flipbook/TE/G${g}-TE-Vol-2/index.html`,
                };
                gradeObj.seLink1 = {
                    displayText: 'Vol 1',
                    link: `https://teacher-files.paradigmmath.com/${pageLanguage}/_flipbook/SE/G${g}-SE-Vol-1/index.html`,
                };
                gradeObj.seLink2 = {
                    displayText: 'Vol 2',
                    link: `https://teacher-files.paradigmmath.com/${pageLanguage}/_flipbook/SE/G${g}-SE-Vol-2/index.html`,
                };
            } else {
                gradeObj.teLink1 = {
                    displayText: 'Vol 1A',
                    link: `https://teacher-files.paradigmmath.com/${pageLanguage}/_flipbook/TE/G${g}-TE-Vol-1A/index.html`,
                };
                gradeObj.teLink2 = {
                    displayText: 'Vol 1B',
                    link: `https://teacher-files.paradigmmath.com/${pageLanguage}/_flipbook/TE/G${g}-TE-Vol-1B/index.html`,
                };
                gradeObj.teLink3 = {
                    displayText: 'Vol 2A',
                    link: `https://teacher-files.paradigmmath.com/${pageLanguage}/_flipbook/TE/G${g}-TE-Vol-2A/index.html`,
                };
                gradeObj.teLink4 = {
                    displayText: 'Vol 2B',
                    link: `https://teacher-files.paradigmmath.com/${pageLanguage}/_flipbook/TE/G${g}-TE-Vol-2B/index.html`,
                };
                gradeObj.seLink1 = {
                    displayText: 'Vol 1',
                    link: `https://teacher-files.paradigmmath.com/${pageLanguage}/_flipbook/SE/G${g}-SE-Vol-1/index.html`,
                };
                gradeObj.seLink2 = {
                    displayText: 'Vol 2',
                    link: `https://teacher-files.paradigmmath.com/${pageLanguage}/_flipbook/SE/G${g}-SE-Vol-2/index.html`,
                };
            }

            return gradeObj;
        });
    }, [user?.allowedGrades, translate, pageLanguage]);

    return (
        <Container>
            <div className="lesson-grades-container">
                <div id="lesson-grades-container">
                    {assignedBooks.map((grade) => (
                        <div key={grade.code} className="grade-card">
                            <h2 className="grade-names">{grade.name}</h2>

                            <Link to={grade.viewAllLessonsLink}>
                                <img src={getGradeCover(grade.code)} alt={grade.name} />
                            </Link>

                            <Link
                                to={grade.viewAllLessonsLink}
                                className={`view-lessons pd-G${grade.code}-primary background`}
                            >
                                {translate('View All Lessons')}
                            </Link>

                            {grade.code !== 'TK' && (
                                <div className="grid grid-cols-[5rem_1fr] gap-x-1">
                                    {/* Row 1: Teacher + links 1 & 2 */}
                                    <div className="text-center">{translate('Teacher')}</div>
                                    <div className="grid grid-cols-2 gap-[2.5px]">
                                        <div className="text-center">
                                            {grade.teLink1 && (
                                                <a
                                                    className="links lesson-page-links"
                                                    href={grade.teLink1.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {grade.teLink1.displayText}
                                                </a>
                                            )}
                                        </div>
                                        <div className="text-center">
                                            {grade.teLink2 && (
                                                <a
                                                    className="links lesson-page-links"
                                                    href={grade.teLink2.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {grade.teLink2.displayText}
                                                </a>
                                            )}
                                        </div>
                                    </div>

                                    {/* Row 2: overflow teacher links 3 & 4 (optional) */}
                                    {grade.teLink3 && (
                                        <>
                                            <div />
                                            <div className="grid grid-cols-2 gap-[2.5px]">
                                                <div className="text-center">
                                                    <a
                                                        className="links lesson-page-links"
                                                        href={grade.teLink3.link}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {grade.teLink3.displayText}
                                                    </a>
                                                </div>
                                                <div className="text-center">
                                                    {grade.teLink4 && (
                                                        <a
                                                            className="links lesson-page-links"
                                                            href={grade.teLink4.link}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            {grade.teLink4.displayText}
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {/* Row 3: Student + links */}
                                    <div className="text-center">{translate('Student')}</div>
                                    <div className="grid grid-cols-2 gap-[2.5px]">
                                        <div className="text-center">
                                            {grade.seLink1 && (
                                                <a
                                                    className="links lesson-page-links"
                                                    href={grade.seLink1.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {grade.seLink1.displayText}
                                                </a>
                                            )}
                                        </div>
                                        <div className="text-center">
                                            {grade.seLink2 && (
                                                <a
                                                    className="links lesson-page-links"
                                                    href={grade.seLink2.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {grade.seLink2.displayText}
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </Container>
    );
};

export default LessonsPage;

function getGradeCover(grade: string): string {
    switch (grade) {
        case 'TK':
            return gTKCover;
        case 'K':
            return gKCover;
        case '1':
            return g1Cover;
        case '2':
            return g2Cover;
        case '3':
            return g3Cover;
        case '4':
            return g4Cover;
        case '5':
            return g5Cover;
        case '6':
            return g6Cover;
        case '6M':
            return g6MCover;
        case '7':
            return g7Cover;
        case '8':
            return g8Cover;
        default:
            return '';
    }
}
